import React, { useEffect, useState } from "react";
import throughShipmentApi, {
  ExternalOrderInfo,
  ExternalOrderLine,
  convertCurrencyType,
  Currency,
} from "@api/throughShipment/throughShipmentApi";
import { Button, message, Modal, Table, Typography } from "antd";
import { PlusCircleOutlined, LinkOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/lib/table";
import { storageTypeOptions } from "@constant/StorageType";
import ExpandIcon from "@assets/expand.png";
import ImportFileModal from "@component/ImportFileModal";
import { useDispatch, useSelector } from "react-redux";
import { hideLoading, showLoading } from "@redux/notifySlice";
import {
  exportExternalProductRevenue,
  externalOrderState,
  fetchExternalOrderInfo,
  fetchExternalOrdersRevenue,
} from "@redux/externalOrderSlice";
import PopupBackground from "@component/PopupBackground";
import { translateErrorsWithDetails } from "@utils/translateError";
import moment from "moment";
import ErrorResultsList from "@component/Common/ErrorResultsList";
import styled from "styled-components";
import { ExpandIconImg, PopupWrapper, Notes, NotesWrapper, Row, Title, PreviewFileBtn } from "./style";

const showExpandIconLength = 60;

export const NotesTruncated = styled.div`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  min-width: 0;
  padding-right: 30px;
`;

const getCharLength = (str: string | null | undefined = "") =>
  Array.from(str ?? "").reduce((len, char) => len + (/[\u4E00-\u9FFF]/.test(char) ? 2 : 1), 0);

type Props = {
  externalOrderInfo: ExternalOrderInfo;
  externalOrderId: number;
  mode: "preview" | "review";
  regionForeign?: boolean;
};

const OrderProductTable = ({ externalOrderInfo, externalOrderId, mode, regionForeign = false }: Props) => {
  const dispatch = useDispatch();
  const { externalOrdersRevenueResult } = useSelector(externalOrderState);
  const externalOrdersRevenueLists = externalOrdersRevenueResult?.results ?? [];
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [showReviewPopup, setShowReviewPopup] = useState(false);

  const clickExpandIcon = (rowId: number) => {
    if (expandedRows.includes(rowId)) {
      setExpandedRows(expandedRows.filter((id) => id !== rowId));
    } else {
      setExpandedRows(expandedRows.concat(rowId));
    }
  };

  const handelUploadXLS = async (file: File) => {
    try {
      dispatch(showLoading());
      const response = await throughShipmentApi.importExternalProductRevenue(externalOrderId, file);
      dispatch(fetchExternalOrderInfo(externalOrderId));
      dispatch(fetchExternalOrdersRevenue(externalOrderId));
      message.success("匯入成功");
      setShowUploadPopup(false);
      return response;
    } catch (error: any) {
      const { result } = error;
      const translatedErrors = translateErrorsWithDetails(result);
      if (translatedErrors.length === 0) return null;
      Modal.warning({
        title: "部分商品資料有誤，請修正",
        content: <ErrorResultsList errorResult={result} />,
        okText: "我知道了",
        width: 600,
      });

      return null;
    } finally {
      dispatch(hideLoading());
      setShowUploadPopup(false);
    }
  };

  const handleOnExport = () => {
    dispatch(exportExternalProductRevenue(externalOrderId));
  };

  const showImportExport =
    mode === "review" &&
    externalOrderInfo.invoiceInfo.invoiceNumber &&
    externalOrderInfo.invoiceInfo.invoiceAmount !== externalOrderInfo.totalRevenue;

  const isExportImportDisabled =
    !externalOrderInfo.invoiceInfo.invoiceDate ||
    moment().isAfter(moment(externalOrderInfo.invoiceInfo.invoiceDate).add(30, "days"));

  const columns: ColumnsType<ExternalOrderLine> = [
    { key: "id", dataIndex: "id", title: "編號" },
    { key: "no", dataIndex: "no", title: "SKU(品號)" },
    { key: "sku", dataIndex: "sku", title: "barcode" },
    { key: "name", dataIndex: "name", title: "品名" },
    { key: "orderedQty", dataIndex: "orderedQty", title: "總數量(pcs)", width: 120 },
    { key: "cost", dataIndex: "cost", title: "商品成本(TWD)", width: 140 },
    { key: "quotedPrice", dataIndex: "quotedPrice", title: "商品報價(TWD)", width: 140 },
    {
      key: "quotedPriceCurrency",
      dataIndex: "quotedPriceCurrency",
      title: `訂單商品外幣報價(${convertCurrencyType(externalOrderInfo.currency)})`,
      width: 190,
    },
    {
      key: "quotedPriceTwd",
      dataIndex: "quotedPriceTwd",
      title: "訂單商品報價(TWD)",
      width: 160,
      render: (_: any, data: ExternalOrderLine) => {
        const isTWD = externalOrderInfo.currency === Currency.TWD;
        const type = isTWD && data.quotedPriceTwd !== data.quotedPrice ? "danger" : undefined;
        return <Typography.Text type={type}>{data.quotedPriceTwd}</Typography.Text>;
      },
    },
    { key: "totalPostDeduction", dataIndex: "totalPostDeduction", title: "後扣金額" },
    {
      key: "margin",
      dataIndex: "margin",
      title: "饌元商品毛利率",
      width: 150,
      render: (data: number) => <div>{`${Math.round(data * 10000) / 100}%`}</div>,
    },
    {
      key: "storageType",
      dataIndex: "storageType",
      title: "倉別",
      render: (value: string) => storageTypeOptions.find((type) => type.key === value)?.name || "-",
    },
    {
      key: "effectiveDateBatch",
      dataIndex: "effectiveDateBatch",
      title: "效期＿批號",
      width: 140,
      render: (_: any, value: ExternalOrderLine) => `${value.effectiveDate || ""}_${value.batchNumber || ""}`,
    },
    { key: "pcsPerCarton", dataIndex: "pcsPerCarton", title: "箱入數" },
    { key: "revenue", dataIndex: "revenue", title: "商品營收" },
    { key: "shippedQty", dataIndex: "shippedQty", title: "實際出貨數量", width: 120 },
    {
      key: "notes",
      dataIndex: "notes",
      title: "備註",
      width: 250,
      render: (data: string, value: ExternalOrderLine) => {
        const isExpanded = !!(data?.length && !expandedRows.includes(value.id));
        const showExpandIcon = getCharLength(data) >= showExpandIconLength;
        return (
          <div>
            <NotesWrapper>
              {!isExpanded ? (
                <Notes isExpanded={isExpanded} id={`${value.id}-note`}>
                  {data}
                </Notes>
              ) : (
                <NotesTruncated id={`${value.id}-note`}>{data}</NotesTruncated>
              )}
            </NotesWrapper>
            {showExpandIcon && <ExpandIconImg src={ExpandIcon} onClick={() => clickExpandIcon(value.id)} />}
          </div>
        );
      },
    },
  ];

  const filterColumns = () => {
    const shouldShowCurrency = mode === "preview" ? regionForeign : externalOrderInfo.currency !== Currency.TWD;
    return columns.filter((item) => {
      if (item.key === "quotedPriceCurrency") {
        return shouldShowCurrency;
      }
      return true;
    });
  };

  const uploadedColumns: ColumnsType<any> = [
    { key: "sku", dataIndex: "sku", title: "sku(品號)" },
    { key: "name", dataIndex: "name", title: "品名" },
    { key: "totalRevenue", dataIndex: "totalRevenue", title: "商品最終總營收(TWD)" },
  ];

  useEffect(() => {
    if (mode === "review") {
      dispatch(fetchExternalOrdersRevenue(externalOrderId));
    }
  }, [dispatch, externalOrderId, mode]);

  return (
    <>
      <div style={{ display: "flex" }}>
        <Title>訂單商品</Title>
        {showImportExport && (
          <>
            <Button type="primary" onClick={handleOnExport} disabled={isExportImportDisabled}>
              匯出商品營收資料
            </Button>
            <Button
              type="primary"
              onClick={() => setShowUploadPopup(true)}
              icon={<PlusCircleOutlined />}
              style={{ marginLeft: "10px" }}
              disabled={isExportImportDisabled}
            >
              匯入商品總營收
            </Button>
            {externalOrdersRevenueLists.length > 0 && (
              <PreviewFileBtn type="link" onClick={() => setShowReviewPopup(true)}>
                <LinkOutlined />
                已匯入檔案
              </PreviewFileBtn>
            )}
          </>
        )}
      </div>
      <Row marginTop="20px">
        <Table
          columns={filterColumns()}
          dataSource={externalOrderInfo.externalOrderLine.filter((item) => item.orderedQty > 0)}
          pagination={false}
          scroll={mode === "preview" ? { x: 2000, y: 325 } : { x: "max-content" }}
        />
      </Row>
      {showUploadPopup && (
        <ImportFileModal
          csvErrorMessage=""
          closePopup={() => setShowUploadPopup(false)}
          clearErrorMessage={() => {}}
          handleUploadFile={handelUploadXLS}
          templatePath="admin/template/xls/external_order_product_revenue_template.xlsx"
          fileType=".xlsx,.xls,csv"
          noticeText="註：匯入上限為 1000 筆"
        />
      )}
      {showReviewPopup && (
        <PopupBackground close={() => setShowReviewPopup(false)}>
          <PopupWrapper>
            <p style={{ paddingBottom: "10px" }}> 總金額：{externalOrderInfo.invoiceInfo.invoiceAmount}</p>
            <Table
              columns={uploadedColumns}
              scroll={{ x: "max-content" }}
              dataSource={externalOrdersRevenueLists}
              pagination={false}
            />
            <Row justifyContent="flex-end" marginTop="20px">
              <Button type="primary" onClick={() => setShowReviewPopup(false)}>
                確定
              </Button>
            </Row>
          </PopupWrapper>
        </PopupBackground>
      )}
    </>
  );
};

export default OrderProductTable;
